import { useQuery } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { MonitorSmartphone, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getAllNinjaDevices, type NinjaDevice } from '@/api/ninjaAdapter.ts'
import {
  Button,
  DataTable,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface DeviceRow extends NinjaDevice {
  customerId: string
  customerName: string
}

function formatLastContact(ts: number | null | undefined): string {
  if (!ts) return '–'
  const d = new Date(ts * 1000)
  return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
}

const columns: ColumnDef<DeviceRow>[] = [
  {
    id: 'status',
    header: 'Status',
    size: 90,
    cell: ({ row }) => (
      <StatusBadge status={row.original.online ? 'online' : 'offline'} size="sm" />
    ),
  },
  {
    accessorKey: 'displayName',
    header: 'Gerät',
    cell: ({ row }) => (
      <span className="font-medium">{row.original.displayName || row.original.systemName || `#${row.original.id}`}</span>
    ),
  },
  {
    accessorKey: 'systemName',
    header: 'Hostname',
    cell: ({ row }) => <span className="text-body text-sm">{row.original.systemName ?? '–'}</span>,
  },
  {
    id: 'customer',
    header: 'Kunde',
    accessorFn: (row) => row.customerName,
    cell: ({ row }) => (
      <Link
        to={`/kunden/${row.original.customerId}`}
        className="text-brand-600 hover:underline text-sm"
      >
        {row.original.customerName}
      </Link>
    ),
  },
  {
    accessorKey: 'lastContact',
    header: 'Letzter Kontakt',
    cell: ({ row }) => (
      <span className={cn('text-sm', !row.original.online && 'text-gray-400')}>
        {formatLastContact(row.original.lastContact)}
      </span>
    ),
  },
]

export function NinjaDevicesPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['ninja-all-devices'],
    queryFn: () => getAllNinjaDevices().then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  })

  const rows: DeviceRow[] = (data ?? []).flatMap((org) =>
    org.devices.map((d) => ({ ...d, customerId: org.customerId, customerName: org.customerName })),
  )

  const orgsWithError = (data ?? []).filter((o) => o.error)
  const online = rows.filter((d) => d.online).length
  const total = rows.length

  return (
    <div className="space-y-6">
      <PageHeader
        title="NinjaOne Geräte"
        description={
          isLoading
            ? 'Lade Geräte…'
            : `${online} von ${total} Geräten online`
        }
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} />
            Aktualisieren
          </Button>
        }
      />

      {isLoading && <LoadingState message="NinjaOne-Geräte werden geladen…" />}

      {isError && (
        <ErrorState
          title="Geräte konnten nicht geladen werden"
          description={(error as Error).message}
        />
      )}

      {orgsWithError.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 space-y-1">
          <p className="font-medium">Fehler bei einzelnen Kunden:</p>
          {orgsWithError.map((o) => (
            <p key={o.customerId}>
              <strong>{o.customerName}:</strong> {o.error}
            </p>
          ))}
        </div>
      )}

      {!isLoading && !isError && (
        <DataTable
          columns={columns}
          data={rows}
          searchPlaceholder="Gerät oder Kunde suchen…"
          emptyState={
            <div className="flex flex-col items-center gap-2 py-12 text-gray-500">
              <MonitorSmartphone className="h-8 w-8 text-gray-300" />
              <p>Keine NinjaOne-Geräte gefunden</p>
              <p className="text-xs text-center max-w-xs">
                Noch keine Kunden mit NinjaOne synchronisiert. Öffne einen Kunden und klicke dort auf den NinjaOne-Sync-Button.
              </p>
            </div>
          }
        />
      )}
    </div>
  )
}
